import Vue from "vue";

export default {
  namespaced: true,
  state: () => ({
    defaultSdkConfigurations: [], // default config values, and general info about configs
    sdkConfigurationForClient: [], // configs that are set different than default
    publishedSdkConfigurationForClient: [], // published configurations
    lastSavedSdkConfigurations: [] // last saved configurations that are set different than default
  }),
  mutations: {
    DEFAULT_SDK_CONFIGURATIONS(state, configs = []) {
      state.defaultSdkConfigurations = configs;
    },
    SDK_CONFIGURATION_FOR_CLIENT(state, configs = []) {
      if (configs["client"] !== undefined) {
        configs["client"]?.forEach((config) => {
          config["isChanged"] = undefined;
        });
      }
      if (configs["sites"] !== undefined) {
        configs["sites"]?.forEach((site) => {
          site.configurations?.forEach((config) => {
            config["isChanged"] = undefined;
          });
        });
      }
      let defaultSdkConfigurationKeys = new Set(state.defaultSdkConfigurations.map((config) => config.key));
      state.sdkConfigurationForClient = configs;
      if (state.sdkConfigurationForClient["client"] !== undefined) {
        state.sdkConfigurationForClient["client"]?.forEach((config, index) => {
          Vue.set(
            state.sdkConfigurationForClient?.["client"]?.[index],
            "isChanged",
            state.publishedSdkConfigurationForClient?.["client"]?.[config.key]?.["value"] !== config.value
          );
        });

        // Add configs that exist in published but not in current client configs
        if (state.publishedSdkConfigurationForClient?.["client"]) {
          const existingKeys = state.sdkConfigurationForClient["client"].map((config) => config.key);
          Object.keys(state.publishedSdkConfigurationForClient["client"])
            .filter((config) => defaultSdkConfigurationKeys.has(config))
            .forEach((publishedKey) => {
              if (!existingKeys.includes(publishedKey)) {
                const newConfig = {
                  key: publishedKey,
                  value: null, // No current value since it doesn't exist in current configs
                  isChanged: true // Mark as changed since it's missing from current configs
                };
                state.sdkConfigurationForClient["client"].push(newConfig);
              }
            });
        }
      }
      if (state.sdkConfigurationForClient["sites"] !== undefined) {
        state.sdkConfigurationForClient["sites"]?.forEach((site, siteIndex) => {
          state.sdkConfigurationForClient["sites"][siteIndex].configurations?.forEach((config, configIndex) => {
            Vue.set(
              state.sdkConfigurationForClient["sites"][siteIndex]["configurations"][configIndex],
              "isChanged",
              state.publishedSdkConfigurationForClient?.["sitesConfigs"]?.[site.siteIdentifier]?.[config.key]?.[
                "value"
              ] !== config.value
            );
          });

          // Add configs that exist in published but not in current site configs
          if (state.publishedSdkConfigurationForClient?.["sitesConfigs"]?.[site.siteIdentifier]) {
            const existingKeys = site.configurations.map((config) => config.key);
            const publishedSiteConfigs = state.publishedSdkConfigurationForClient["sitesConfigs"][site.siteIdentifier];

            Object.keys(publishedSiteConfigs)
              .filter((config) => defaultSdkConfigurationKeys.has(config))
              .forEach((publishedKey) => {
                if (!existingKeys.includes(publishedKey)) {
                  const newConfig = {
                    key: publishedKey,
                    value: null, // No current value since it doesn't exist in current configs
                    isChanged: true // Mark as changed since it's missing from current configs
                  };
                  state.sdkConfigurationForClient["sites"][siteIndex].configurations.push(newConfig);
                }
              });
          }

          if (state.sdkConfigurationForClient["sites"][siteIndex]?.buildings?.length > 0) {
            state.sdkConfigurationForClient["sites"][siteIndex]["buildings"]?.forEach((building, buildingIndex) => {
              state.sdkConfigurationForClient["sites"][siteIndex]["buildings"][buildingIndex]?.configurations?.forEach(
                (config, configIndex) => {
                  Vue.set(
                    state.sdkConfigurationForClient["sites"][siteIndex]["buildings"][buildingIndex]["configurations"][
                      configIndex
                    ],
                    "isChanged",
                    state.publishedSdkConfigurationForClient?.["buildingConfigs"]?.[building.buildingIdentifier]?.[
                      config.key
                    ]?.["value"] !== config.value
                  );
                }
              );

              // Add configs that exist in published but not in current building configs
              if (state.publishedSdkConfigurationForClient?.["buildingConfigs"]?.[building.buildingIdentifier]) {
                const existingKeys = building.configurations.map((config) => config.key);
                const publishedBuildingConfigs =
                  state.publishedSdkConfigurationForClient["buildingConfigs"][building.buildingIdentifier];

                Object.keys(publishedBuildingConfigs)
                  .filter((config) => defaultSdkConfigurationKeys.has(config))
                  .forEach((publishedKey) => {
                    if (!existingKeys.includes(publishedKey)) {
                      const newConfig = {
                        key: publishedKey,
                        value: null, // No current value since it doesn't exist in current configs
                        isChanged: true // Mark as changed since it's missing from current configs
                      };
                      state.sdkConfigurationForClient["sites"][siteIndex]["buildings"][
                        buildingIndex
                      ].configurations.push(newConfig);
                    }
                  });
              }
            });
          }
        });
      }
    },
    PUBLISHED_SDK_CONFIGURATION_FOR_CLIENT(state, configs = []) {
      if (configs["client"] !== undefined) {
        configs["client"] = configs?.["client"]?.reduce(function (r, e) {
          r[e.key] = e;
          return r;
        }, {});
      }
      if (configs["sites"] !== undefined) {
        configs["sitesConfigs"] = {};
        configs["buildingConfigs"] = {};
        configs["sites"].forEach((site) => {
          configs["sitesConfigs"][site.siteIdentifier] = site.configurations?.reduce(function (r, e) {
            r[e.key] = e;
            return r;
          }, {});
          site?.buildings?.forEach((building) => {
            configs["buildingConfigs"][building.buildingIdentifier] = building.configurations.reduce(function (r, e) {
              r[e.key] = e;
              return r;
            }, {});
          });
        });
      }
      delete configs["sites"];
      state.publishedSdkConfigurationForClient = configs;
    },
    LAST_SAVED_SDK_CONFIGURATIONS(state, configs = []) {
      state.lastSavedSdkConfigurations = configs;
    }
  },
  actions: {
    CLEAR_ALL_CONFIGURATIONS({ commit }) {
      commit("DEFAULT_SDK_CONFIGURATIONS");
      commit("SDK_CONFIGURATION_FOR_CLIENT");
      commit("LAST_SAVED_SDK_CONFIGURATIONS");
    }
  },
  getters: {
    isConfigChanged: (state) => (configurationKey, value, siteInternalIdentifier, buildingInternalIdentifier) => {
      let lastSaved;
      // if no identifier given, it is a client config, otherwise site or building specific override
      if (siteInternalIdentifier === undefined && buildingInternalIdentifier === undefined) {
        lastSaved = state.lastSavedSdkConfigurations["client"];
      } else {
        const siteConfig = state.lastSavedSdkConfigurations.sites.find(
          (siteConfig) => siteConfig.siteIdentifier === siteInternalIdentifier
        );
        if (buildingInternalIdentifier === undefined) {
          lastSaved = siteConfig.configurations;
        } else {
          lastSaved = siteConfig.buildings.find(
            (buildingConfig) => buildingConfig.buildingIdentifier === buildingInternalIdentifier
          ).configurations;
        }
      }
      // if the same key exists in lastSavedSdkConfigurations and the value is different, return true
      const lastSavedConfig = lastSaved.find((config) => config.configurationKey === configurationKey);
      if (lastSavedConfig !== undefined) {
        return lastSavedConfig.configurationValue !== value;
      }
      // if the key does not exist in lastSavedSdkConfigurations, but the value is different from the default (in defaultSdkConfigurations), return true
      return (
        state.defaultSdkConfigurations.find((config) => config.configurationKey === configurationKey).value !== value
      );
    },
    isConfigWaitingForPublish: (state) => (configurationKey, siteInternalIdentifier, buildingInternalIdentifier) => {
      // if no identifier given, it is a client config, otherwise site or building specific override
      if (siteInternalIdentifier === undefined && buildingInternalIdentifier === undefined) {
        return state.sdkConfigurationForClient?.client?.find((config) => config.key === configurationKey)?.isChanged;
      } else {
        const siteConfig = state.sdkConfigurationForClient?.sites?.find(
          (site) => site.siteIdentifier === siteInternalIdentifier
        );
        if (buildingInternalIdentifier === undefined) {
          return siteConfig?.configurations?.find((config) => config.key === configurationKey)?.isChanged;
        } else {
          const buildingConfig = siteConfig?.buildings?.find(
            (building) => building.buildingIdentifier === buildingInternalIdentifier
          );
          // building
          return buildingConfig?.configurations?.find((config) => config.key === configurationKey)?.isChanged;
        }
      }
    },
    isConfigInPublishedData: (state) => (configurationKey, siteInternalIdentifier, buildingInternalIdentifier) => {
      // Check client level configurations
      if (siteInternalIdentifier === undefined && buildingInternalIdentifier === undefined) {
        return state.publishedSdkConfigurationForClient?.client?.[configurationKey] !== undefined;
      }

      // Check site level configurations
      if (buildingInternalIdentifier === undefined) {
        return (
          state.publishedSdkConfigurationForClient?.sitesConfigs?.[siteInternalIdentifier]?.[configurationKey] !==
          undefined
        );
      }

      // Check building level configurations
      return (
        state.publishedSdkConfigurationForClient?.buildingConfigs?.[buildingInternalIdentifier]?.[configurationKey] !==
        undefined
      );
    },
    isConfigOnlyInDraftData: (state) => (configurationKey, siteInternalIdentifier, buildingInternalIdentifier) => {
      if (siteInternalIdentifier === undefined && buildingInternalIdentifier === undefined) {
        // Check client level configurations
        const config = state.sdkConfigurationForClient?.client?.find((config) => config.key === configurationKey);
        return config !== undefined && config.value !== null;
      }

      // Check site level configurations
      if (buildingInternalIdentifier === undefined) {
        const site = state.sdkConfigurationForClient?.sites?.find(
          (site) => site.siteIdentifier === siteInternalIdentifier
        );
        const config = site?.configurations?.find((config) => config.key === configurationKey);
        return config !== undefined && config.value !== null;
      }

      // Check building level configurations
      const site = state.sdkConfigurationForClient?.sites?.find(
        (site) => site.siteIdentifier === siteInternalIdentifier
      );
      const building = site?.buildings?.find((building) => building.buildingIdentifier === buildingInternalIdentifier);
      const config = building?.configurations?.find((config) => config.key === configurationKey);
      return config !== undefined && config.value !== null;
    }
  }
};
